import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@shared/components/common';
import { useAnalytics } from '@core/analytics';

export interface GroupHeroProps {
  logoImage?: string;
  groupSlug: string;
}

export function GroupHero({ logoImage, groupSlug }: GroupHeroProps) {
  const { t } = useTranslation('booking');
  const { trackEvent } = useAnalytics();

  const rawHeadline = t('SELECT_RESTAURANT_SCREEN_TEXT_1');

  // "Blank" means: empty string, or the key itself (what a pass-through mock
  // returns), so both production misconfiguration and test stubs are caught.
  const headlineIsBlank =
    !rawHeadline || rawHeadline === 'SELECT_RESTAURANT_SCREEN_TEXT_1';

  const headline = headlineIsBlank ? t('GROUP_HERO_FALLBACK_HEADLINE') : rawHeadline;

  useEffect(() => {
    if (headlineIsBlank) {
      trackEvent({
        name: 'group_headline_missing',
        properties: { group_slug: groupSlug },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headlineIsBlank, groupSlug]);

  return (
    <header
      data-testid="group-hero"
      data-verify-unit="GroupHero"
      data-verify-has-logo={String(!!logoImage)}
      data-verify-headline-blank={String(headlineIsBlank)}
      className="w-full bg-background flex flex-col items-center gap-5 px-6 pt-5 pb-6"
    >
      <div
        data-testid="group-hero-row"
        className="grid w-full grid-cols-3 items-center"
      >
        <span aria-hidden="true" />
        <div className="flex justify-center">
          {logoImage && (
            <img
              src={logoImage}
              alt={t('GROUP_HERO_LOGO_ALT')}
              className="h-16 max-w-[220px] object-contain"
            />
          )}
        </div>
        <div className="flex justify-end">
          <LanguageSelector size="sm" />
        </div>
      </div>

      <h1 className="text-3xl sm:text-4xl font-display tracking-wide text-center text-foreground max-w-lg">
        {headline}
      </h1>
    </header>
  );
}

export default GroupHero;
